import { useState } from 'react'
import { useApps } from '../context/AppsContext'
import { Button, Skeleton, Card, PageHeader, EmptyState } from '../components'
import AppCard from '../components/AppCard'
import AppRow from '../components/AppRow'
import CreateAppModal from '../components/CreateAppModal'
import AppSettingsModal from '../components/AppSettingsModal'
import { appsApi, pingsApi } from '../api'
import type { App, AppWithStats } from '../types'
import { Plus, LayoutGrid, List, Radio, Loader2 } from 'lucide-react'
import toast from 'react-hot-toast'

type ViewMode = 'grid' | 'list'

function AppCardSkeleton() {
  return (
    <Card className="p-6">
      <div className="flex items-start gap-3 mb-4">
        <Skeleton className="w-5 h-5 rounded-full" />
        <div className="flex-1">
          <Skeleton className="h-5 w-32 mb-2" />
          <Skeleton className="h-4 w-48" />
        </div>
      </div>
      <div className="grid grid-cols-3 gap-4 mt-4">
        <div>
          <Skeleton className="h-3 w-16 mb-1" />
          <Skeleton className="h-6 w-8" />
        </div>
        <div>
          <Skeleton className="h-3 w-16 mb-1" />
          <Skeleton className="h-6 w-8" />
        </div>
        <div>
          <Skeleton className="h-3 w-16 mb-1" />
          <Skeleton className="h-6 w-12" />
        </div>
      </div>
    </Card>
  )
}

function AppRowSkeleton() {
  return (
    <div className="flex items-center gap-4 px-4 py-3 bg-white border-b border-slate-100">
      <Skeleton className="w-4 h-4 rounded-full flex-shrink-0" />
      <div className="flex-1 min-w-0">
        <Skeleton className="h-5 w-32 mb-1" />
        <Skeleton className="h-4 w-48" />
      </div>
      <div className="flex items-center gap-6 flex-shrink-0">
        <Skeleton className="w-16 h-5" />
        <Skeleton className="w-16 h-5" />
        <Skeleton className="w-12 h-5" />
      </div>
      <Skeleton className="w-6 h-6 flex-shrink-0" />
    </div>
  )
}

export default function AppsDashboardPage() {
  const { apps, createApp, updateApp, deleteApp, isLoading, refreshApps } = useApps()
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [isSettingsOpen, setIsSettingsOpen] = useState(false)
  const [selectedApp, setSelectedApp] = useState<App | null>(null)
  const [viewMode, setViewMode] = useState<ViewMode>(() => {
    const saved = localStorage.getItem('falcon-apps-view-mode')
    return saved === 'list' || saved === 'grid' ? saved : 'grid'
  })

  const handleViewModeChange = (mode: ViewMode) => {
    setViewMode(mode)
    localStorage.setItem('falcon-apps-view-mode', mode)
  }

  const [isPingingAll, setIsPingingAll] = useState(false)

  const handlePing = async (appId: string) => {
    try {
      const ping = await pingsApi.trigger(appId)
      if (ping.is_success) {
        toast.success('Health check passed')
      } else {
        toast.error(ping.error_message || 'Health check failed')
      }
      // Refresh to update status indicators
      await refreshApps()
    } catch {
      toast.error('Failed to trigger ping')
    }
  }

  const handlePingAll = async () => {
    const appsWithHealth = apps.filter(app => app.health_url)
    if (appsWithHealth.length === 0) {
      toast.error('No apps with health URLs configured')
      return
    }

    setIsPingingAll(true)
    let successCount = 0
    let failCount = 0

    try {
      await Promise.all(
        appsWithHealth.map(async app => {
          try {
            const ping = await pingsApi.trigger(app.id)
            if (ping.is_success) {
              successCount++
            } else {
              failCount++
            }
          } catch {
            failCount++
          }
        })
      )

      if (failCount === 0) {
        toast.success(`All ${successCount} apps healthy`)
      } else if (successCount === 0) {
        toast.error(`All ${failCount} apps failed`)
      } else {
        toast(`${successCount} healthy, ${failCount} failed`, { icon: '⚠️' })
      }
      // Refresh to update status indicators
      await refreshApps()
    } finally {
      setIsPingingAll(false)
    }
  }

  const handleOpenSettings = async (app: AppWithStats) => {
    try {
      // Fetch full app details with all settings
      const fullApp = await appsApi.get(app.id)
      setSelectedApp(fullApp)
      setIsSettingsOpen(true)
    } catch {
      toast.error('Failed to load app settings')
    }
  }

  return (
    <>
      <PageHeader
        title="Your Apps"
        subtitle="Monitor errors, uptime, and performance across all your applications"
        action={
          <div className="flex items-center gap-3">
            {/* Ping All button */}
            {apps.some(app => app.health_url) && (
              <Button
                variant="outline"
                onClick={handlePingAll}
                disabled={isPingingAll}
                className="flex items-center gap-2"
              >
                {isPingingAll ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Radio className="w-4 h-4" />
                )}
                {isPingingAll ? 'Pinging...' : 'Ping All'}
              </Button>
            )}
            {/* View mode toggle */}
            <div className="flex items-center bg-slate-100 rounded-lg p-1">
              <button
                onClick={() => handleViewModeChange('grid')}
                className={`p-1.5 rounded transition-colors ${
                  viewMode === 'grid'
                    ? 'bg-white text-slate-900 shadow-sm'
                    : 'text-slate-500 hover:text-slate-700'
                }`}
                title="Grid view"
              >
                <LayoutGrid className="w-4 h-4" />
              </button>
              <button
                onClick={() => handleViewModeChange('list')}
                className={`p-1.5 rounded transition-colors ${
                  viewMode === 'list'
                    ? 'bg-white text-slate-900 shadow-sm'
                    : 'text-slate-500 hover:text-slate-700'
                }`}
                title="List view"
              >
                <List className="w-4 h-4" />
              </button>
            </div>
            <Button
              variant="primary"
              onClick={() => setIsModalOpen(true)}
              className="flex items-center gap-2"
            >
              <Plus className="w-5 h-5" />
              New App
            </Button>
          </div>
        }
      />

      {isLoading ? (
        viewMode === 'grid' ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[...Array(6)].map((_, i) => (
              <AppCardSkeleton key={i} />
            ))}
          </div>
        ) : (
          <Card className="overflow-hidden">
            {[...Array(6)].map((_, i) => (
              <AppRowSkeleton key={i} />
            ))}
          </Card>
        )
      ) : apps.length === 0 ? (
        <EmptyState
          icon={<Plus className="w-6 h-6" />}
          title="No apps yet"
          description="Create your first app to start monitoring errors, uptime, and custom metrics"
          action={{
            label: 'Create Your First App',
            onClick: () => setIsModalOpen(true),
          }}
        />
      ) : viewMode === 'grid' ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {apps.map(app => (
            <AppCard
              key={app.id}
              app={app}
              onDelete={deleteApp}
              onSettings={handleOpenSettings}
              onPing={handlePing}
            />
          ))}
        </div>
      ) : (
        <Card className="overflow-hidden">
          {/* List header */}
          <div className="flex items-center gap-4 px-4 py-2 bg-slate-50 border-b border-slate-200 text-xs font-medium text-slate-500 uppercase tracking-wide">
            <div className="w-4 flex-shrink-0"></div>
            <div className="flex-1">App</div>
            <div className="flex items-center gap-6 flex-shrink-0">
              <div className="w-20 text-center">24h Errors</div>
              <div className="w-20 text-center">Open</div>
              <div className="w-16 text-center">Uptime</div>
            </div>
            <div className="w-6 flex-shrink-0"></div>
          </div>
          {apps.map(app => (
            <AppRow
              key={app.id}
              app={app}
              onDelete={deleteApp}
              onSettings={handleOpenSettings}
              onPing={handlePing}
            />
          ))}
        </Card>
      )}

      <CreateAppModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSubmit={createApp}
      />

      {selectedApp && (
        <AppSettingsModal
          isOpen={isSettingsOpen}
          onClose={() => {
            setIsSettingsOpen(false)
            setSelectedApp(null)
          }}
          app={selectedApp}
          onSubmit={updateApp}
        />
      )}
    </>
  )
}
